import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatPoints, timeLeft } from "@/lib/format";
import { useI18n } from "@/lib/i18n";
import { Users, Clock } from "lucide-react";

export default function MarketCard({ market }) {
    const { t, lang } = useI18n();
    if (!market) return null;
    const topOption = [...(market.options || [])].sort((a, b) => b.current_pool - a.current_pool)[0];
    return (
        <Link to={`/mercado/${market.id}`} data-testid={`market-card-${market.id}`}>
            <Card className="group relative overflow-hidden bg-p2w-surface border-p2w-line hover:border-p2w-green/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-glow-green p-5 rounded-2xl h-full flex flex-col">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        {market.category && (
                            <Badge
                                variant="outline"
                                className="border-p2w-line text-p2w-gray bg-p2w-elevated text-[10px] uppercase tracking-wider font-semibold"
                            >
                                {market.category.name}
                            </Badge>
                        )}
                        {market.is_featured && (
                            <Badge className="bg-p2w-purple/20 text-p2w-purple border-p2w-purple/40 text-[10px] uppercase tracking-wider">
                                {t("market.featured")}
                            </Badge>
                        )}
                    </div>
                    {market.is_live && (
                        <div className="flex items-center gap-1.5">
                            <span className="w-2 h-2 rounded-full bg-p2w-magenta animate-pulse-dot shadow-glow-magenta" />
                            <span className="text-[10px] uppercase tracking-widest font-sora font-bold text-p2w-magenta">
                                {t("market.live")}
                            </span>
                        </div>
                    )}
                    {market.is_closed && (
                        <Badge className="bg-p2w-red/15 text-p2w-red border-p2w-red/40 text-[10px] uppercase tracking-wider font-bold" data-testid="market-closed-badge">
                            Cerrado
                        </Badge>
                    )}
                </div>

                <h3 className="font-sora font-bold text-lg leading-snug mb-4 text-white group-hover:text-p2w-green transition-colors">
                    {market.title}
                </h3>

                <div className="space-y-2 mb-4 flex-1">
                    {(market.options || []).slice(0, 3).map((o) => {
                        const isTop = topOption && o.id === topOption.id;
                        const pct = market.total_pool > 0 ? (o.current_pool / market.total_pool) * 100 : 0;
                        return (
                            <div
                                key={o.id}
                                className={`relative overflow-hidden rounded-xl border px-3 py-2.5 flex items-center justify-between ${
                                    isTop
                                        ? "border-p2w-green/40 bg-p2w-green/5"
                                        : "border-p2w-line bg-p2w-elevated"
                                }`}
                            >
                                <div
                                    className={`absolute inset-y-0 left-0 ${
                                        isTop ? "bg-p2w-green/10" : "bg-white/[0.03]"
                                    }`}
                                    style={{ width: `${pct}%` }}
                                />
                                <span className="relative z-10 text-sm font-medium text-white/90 truncate pr-2">
                                    {o.label}
                                </span>
                                <span
                                    className={`relative z-10 font-sora font-bold text-sm number-tabular ${
                                        isTop ? "text-p2w-green" : "text-white"
                                    }`}
                                >
                                    {o.odds > 0 ? `${o.odds.toFixed(2)}x` : "—"}
                                </span>
                            </div>
                        );
                    })}
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-p2w-line text-xs text-p2w-gray">
                    <div className="flex items-center gap-1.5">
                        <span className="font-sora font-bold text-p2w-green number-tabular">
                            {formatPoints(market.total_pool)}
                        </span>
                        <span>pts</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            <span className="number-tabular">{market.participants || 0}</span>
                        </div>
                        <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            <span>{timeLeft(market.closes_at, lang)}</span>
                        </div>
                    </div>
                </div>
            </Card>
        </Link>
    );
}
